import { ReactNode, useEffect, useState } from "react";
import {
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import CrudField from "@/components/tables/fields/CrudField";
import { useForm, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import style from "@/components/tables/genericCrudTableModal.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { CrudErrorContext } from "@/components/tables/crudErrorContext";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";
import {
  buildFormColumns,
  buildInitialFormValues,
} from "@/components/tables/modal/modalFormSetup";
import { useValidUntilAutoCalc } from "@/components/tables/modal/useValidUntilAutoCalc";
import type { EntityAdapter } from "@/components/tables/entityAdapters/entityAdapter";

export type AdditionalComponentForModal =
  | ReactNode
  | ((modalContext: {
      modalMode?: string;
      presetValues?: Record<string, any>;
    }) => ReactNode);

interface Props {
  columns?: any;
  title?: string;
  submitButtonText?: string;
  presetValues?: any;
  onSubmit?: (values: any, resetFunction?: () => void) => void;
  onClose?: () => void;
  companySubjectId?: string;
  companyId?: any;
  open: boolean;
  modalMode?: string;
  customModalStyle?: any;
  useTwoColumnModalLayout?: boolean;
  userEmails?: string[];
  additionalColumnsForCreate?: any;
  additionalColumnsForEdit?: any;
  subjectIdForBuildings?: any;
  additionalComponentForModal?: AdditionalComponentForModal;
  limit?: string;
  entityOfTable?: string;
  entityAdapter?: EntityAdapter;
  handleError?: (error: any) => void;
}

export default function GenericCrudTableModal(props: Props) {
  const { t } = useTranslation();
  const [formColumns, setFormColumns] = useState<any>([]);
  const methods = useForm<any>({
    mode: "onChange",
    shouldFocusError: false,
  });
  const { registerValidationTarget, focusFirstInvalidField } = useFocusFirstInvalidField({
    fieldOrder: formColumns.map((column: any) => column.id),
    focusableSelector: 'input:not([type="hidden"]), textarea, select, [role="combobox"], [data-validation-focus-target]',
  });

  const handleClose = () => {
    props.onClose?.();
    methods.clearErrors();
    methods.reset();
  };

  let title = t("add_new_element");
  let submitButtonText = t("add");

  if (props.title) {
    title = props.title;
  }
  if (props.submitButtonText) {
    submitButtonText = props.submitButtonText;
  }

  useEffect(() => {
    const newFormColumns = buildFormColumns({
      columns: props.columns,
      additionalColumnsForCreate: props.additionalColumnsForCreate,
      additionalColumnsForEdit: props.additionalColumnsForEdit,
      modalMode: props.modalMode,
    });
    const newValues = buildInitialFormValues(
      newFormColumns,
      props.presetValues,
      props.modalMode,
    );

    setFormColumns(newFormColumns);

    if (Object.keys(newValues).length > 0) {
      methods.reset(newValues);
    }
  }, [
    props.presetValues,
    props.columns,
    props.modalMode,
    props.additionalColumnsForCreate,
    props.additionalColumnsForEdit,
    methods,
  ]);

  const { recalculateValidUntil } = useValidUntilAutoCalc({
    enabled: props.entityAdapter?.autoCalculatesValidUntil ?? false,
    open: props.open,
    getValues: methods.getValues,
    setValue: methods.setValue,
  });

  const handleManualFormSubmit = async (val: any, reset: any) => {
    if (props?.onSubmit) {
      try {
        await props?.onSubmit?.(val, reset);
      } catch {
        //errorhandling is done in the onSubmit in parent. Error not thrown again
      }
    }
  };

  const { handleSubmit, isSubmitting } = useFormSubmission({
    disabled: !props.open,
    onSubmit: methods.handleSubmit(
      (data) => handleManualFormSubmit(data, methods.reset),
      (errors) => {
        if (props.open) focusFirstInvalidField(errors);
      },
    ),
  });

  const additionalComponentForModal =
    typeof props.additionalComponentForModal === "function"
      ? props.additionalComponentForModal({
          modalMode: props.modalMode,
          presetValues: props.presetValues,
        })
      : props.additionalComponentForModal;

  const content = (
    <FormProvider {...methods}>
      <form noValidate onSubmit={handleSubmit}>
        <Stack
          className={style.stackFormWrapper}
          style={{
            ...props.customModalStyle,
            ...(props.useTwoColumnModalLayout
              ? {
                  alignItems: "center",
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(min(100%, 20rem), 1fr))",
                }
              : {}),
          }}
        >
          {formColumns.map((column: any) => {
            const fieldKey = column.id + "_" + column.header;
            const formField = (
              <CrudField
                field={column}
                registerValidationTarget={registerValidationTarget}
                additionalComponentForModal={additionalComponentForModal}
                subjectIdForBuildings={props.subjectIdForBuildings}
                modalMode={props.modalMode}
                companySubjectId={props.companySubjectId}
                key={fieldKey}
                userEmails={props.userEmails}
                entityOfTable={props.entityOfTable}
                convertsPriceToEurocent={props.entityAdapter?.convertsPriceToEurocent}
                onChange={(value: any) => recalculateValidUntil(column.id, value)}
                limit={props.limit}
              />
            );

            if (
              !props.useTwoColumnModalLayout ||
              !column.modalFullWidth ||
              column.hideField
            ) {
              return formField;
            }

            return (
              <div className={style.fullWidthField} key={fieldKey}>
                {formField}
              </div>
            );
          })}
        </Stack>
        <DialogActions sx={{ p: "0.75rem", justifyContent: "space-between" }}>
          <GenericButton variant="outlined" onClick={handleClose}>
            {t("cancel")}
          </GenericButton>

          <GenericButton
            variant="contained"
            type="submit"
            loading={isSubmitting}
            showSpinnerOnDisable={true}
          >
            {submitButtonText}
          </GenericButton>
        </DialogActions>
      </form>
    </FormProvider>
  );

  const cancelClose = useModalDismiss(props.open, handleClose);

  return (
    <CrudErrorContext.Provider value={props.handleError ?? null}>
      <Dialog
        open={props.open}
        {...cancelClose}
        PaperProps={{ sx: { maxHeight: "95%" } }}
        className={style.dialogContainer}
        sx={{
          "& .MuiPaper-root": {
            maxWidth: "80vw",
          },
        }}
      >
        <DialogTitle textAlign="center">{title}</DialogTitle>
        <DialogContent sx={{ overflowY: "visible" }}>{content}</DialogContent>
      </Dialog>
    </CrudErrorContext.Provider>
  );
}
